import React, { useEffect, useRef, useState } from 'react';
import { capturesService, Capture } from 'api/services/captures.service';
import { X, Mic, Play, Download, Hash, ArrowRightLeft, Phone, PhoneForwarded, XCircle, Bot as BotIcon, User, Clock } from 'lucide-react';
import { AudioSession, CallLog, Recording, DtmfEvent } from 'types';
import { callsService } from 'api/services/calls.service';
import { recordingsService } from 'api/services/recordings.service';
import { formatDate, maskSensitiveNumbers, parseTranscript, formatTimestamp } from 'utils/helpers';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import TranscriptViewer from 'components/Common/TranscriptViewer';
import AudioPlayer from 'components/Common/AudioPlayer';
import CallTranscriptPanel from 'components/Calls/CallTranscriptPanel';
import { seekAudio } from 'utils/callTranscript';

interface AudioSessionModalProps {
  session: AudioSession;
  onClose: () => void;
  organizationId?: string; // Organization ID for proper tenant routing
}

const AudioSessionModal: React.FC<AudioSessionModalProps> = ({ session, onClose, organizationId }) => {
  const [call, setCall] = useState<CallLog | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPlayer, setShowPlayer] = useState(false);
  const [authenticatedUrl, setAuthenticatedUrl] = useState<string>('');
  // The open player's <audio>, so a transcript timestamp can seek it.
  const audioEl = useRef<HTMLAudioElement | null>(null);
  // Why a recording could not be played, in words. Previously this only ever
  // reached console.error, so pressing play on a call with no recording did
  // nothing at all and looked like a broken button.
  const [playbackError, setPlaybackError] = useState<string | null>(null);
  // Whether the reader ASKED for audio, as opposed to the player simply being
  // resolved when the dialog opened. Only a press may start playback: a dialog
  // that begins talking on its own is worse than one that needs a click.
  const [pressedPlay, setPressedPlay] = useState(false);

  // The recording, resolved AS THE MODAL OPENS rather than on a button press.
  //
  // The listing page behind this already plays the same audio from the same
  // call id, so making the reader press Play here — and hiding that button
  // whenever the separate getCallDetail fetch came back empty — was the modal
  // being less capable than the page that opened it.
  //
  // session.call_id is the source that actually survives: a capture opened from
  // a lead has no call_log row, which is why `call` is so often null here.
  // Silent on failure: playbackError already carries the reason, and plenty of
  // calls legitimately have no recording.
  useEffect(() => {
    const callId = (call as any)?.call_id || (call as any)?.unique_id
                || (session as any)?.call_id;
    const storedUrl = (call as any)?.recording_url || (session as any)?.recording_url;
    if (!callId && !storedUrl) return;
    let alive = true;
    recordingsService.resolvePlayableUrl(callId, storedUrl, organizationId)
      .then((url) => {
        if (!alive || !url) return;
        setAuthenticatedUrl(url);
        setShowPlayer(true);
        setPlaybackError(null);
      })
      .catch(() => { /* the Play button remains, and says why when pressed */ });
    return () => { alive = false; };
  }, [call, session, organizationId]);

  // What this call captured — fetched when the modal opens, never joined into
  // the list behind it. Most calls capture nothing, so this is usually empty and
  // renders nothing at all.
  const [captured, setCaptured] = useState<Capture[]>([]);
  useEffect(() => {
    const callId = (call as any)?.call_id || (call as any)?.unique_id;
    if (!callId) { setCaptured([]); return; }
    let alive = true;
    capturesService.forCall(String(callId)).then(r => {
      if (alive && r.success) setCaptured((r.data as Capture[]) || []);
    });
    return () => { alive = false; };
  }, [call]);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);

      try {
        // Fetch related call - use organizationId prop for proper access control
        const callResponse = await callsService.getCallDetail(
          session.call_log_id,
          organizationId
        );
        if (callResponse.success && callResponse.data) {
          setCall(callResponse.data);
        }

        // Fetch recording
        // const recordingResponse = await recordingsService.getRecordings({
        //   sessionId: session.id,
        //   organizationId: organizationId,
        //   perPage: 1
        // });

        // if (recordingResponse.success && recordingResponse.data?.data && recordingResponse.data.data.length > 0) {
        //   setRecording(recordingResponse.data.data[0]);
        // }
      } catch (error) {
        console.error('Failed to fetch details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [session.id, session.call_log_id, organizationId]);

  const handlePlayRecording = async () => {
    setPressedPlay(true);
    // NOT `if (!call) return`.
    //
    // `call` is a separate fetch — getCallDetail(session.call_log_id) — and it
    // comes back empty on exactly the route that needs this most: opened from a
    // lead, where the capture carries a call id but no call_log row. The
    // transcript rendered (it comes from `session`) and the player did not, so
    // the call looked like it had no audio.
    //
    // The session knows its own call id. Use it when the call detail is absent,
    // rather than refusing to look.

    // Prefer the server-signed copy, whose key carries this caller's own
    // organisation, and fall back to the stored URL with the session token
    // appended where cpass holds no copy — that fallback is what the older
    // backends serve, and dropping it left those portals with no audio at all.
    try {
      const callId = (call as any)?.call_id || (call as any)?.unique_id
                  || (session as any)?.call_id;
      const storedUrl = (call as any)?.recording_url || (session as any)?.recording_url;
      if (!callId && !storedUrl) {
        setPlaybackError('This call has no id, so its recording cannot be located.');
        return;
      }
      const url = await recordingsService.resolvePlayableUrl(callId, storedUrl, organizationId);
      if (!url) {
        // An ordinary outcome, not a failure: plenty of calls have no recording.
        setPlaybackError('No recording is held for this call.');
        return;
      }
      setPlaybackError(null);
      setAuthenticatedUrl(url);
      setShowPlayer(true);
    } catch (error) {
      setPlaybackError('The recording could not be opened.');
      console.error('Play failed:', error);
    }
  };

    const handleDownloadRecording = async () => {
      // The SAME ladder the play button walks, not the stored URL with the
      // session token on it. On this estate the stored URL names
      // recordings.greengiraffe.in, which wants the token cpass mints as `?t=`
      // and reads `?token=` as no token — so the old call produced a link that
      // 401'd on every call you could happily listen to. Guarding on
      // `recording_url` was the same mistake: a call cpass can serve does not
      // need one.
      try {
        const callId = (call as any)?.call_id || (call as any)?.unique_id
                    || (session as any)?.call_id;
        const storedUrl = (call as any)?.recording_url || (session as any)?.recording_url;
        if (!callId && !storedUrl) return;

        const url = await recordingsService.resolveDownloadUrl(callId, storedUrl, organizationId);
        if (!url) {
          setPlaybackError('No recording is held for this call.');
          return;
        }
        setPlaybackError(null);
        // .wav, because that is what the switch writes and what the CDN serves.
        // Naming it .mp3 handed people a file their player refused to open.
        recordingsService.startDownload(url, `recording-${callId || (session as any)?.session_ref}.wav`);
      } catch (error) {
        setPlaybackError('The recording could not be downloaded.');
        console.error('Download failed:', error);
      }
    };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="p-6 border-b flex items-center justify-between flex-shrink-0">
          <h3 className="text-xl font-bold">Audio Session: {session.session_ref}</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <>
              {/* Session Overview */}
              <div className="grid grid-cols-2 gap-4 bg-blue-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Session Reference</p>
                  <p className="font-medium font-mono">{session.session_ref}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Call ID</p>
                  <p className="font-medium text-blue-600">
                    {call?.unique_id || `CALL-${session.call_log_id}`}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Duration</p>
                  <p className="font-medium">{(session.audio_duration_ms / 1000).toFixed(1)}s</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Connection Start</p>
                  <p className="font-medium text-xs">{formatDate(session.connection_start)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Connection End</p>
                  <p className="font-medium text-xs">{session.connection_end ? formatDate(session.connection_end) : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Transcription Events</p>
                  <p className="font-medium">{session.transcription_events}</p>
                </div>
              </div>

              {/* Call Details */}
              {(session.caller_id || session.dialed_number || session.call_type) && (
                <div className="grid grid-cols-2 gap-4 bg-gray-50 p-4 rounded-lg">
                  <h4 className="col-span-2 font-semibold mb-2 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-blue-600" />
                    Call Details
                  </h4>
                  {session.caller_id && (
                    <div>
                      <p className="text-sm text-gray-600">Caller ID</p>
                      <p className="font-medium">{session.caller_id}</p>
                    </div>
                  )}
                  {session.dialed_number && (
                    <div>
                      <p className="text-sm text-gray-600">Dialed Number</p>
                      <p className="font-medium">{session.dialed_number}</p>
                    </div>
                  )}
                  {session.call_type && (
                    <div>
                      <p className="text-sm text-gray-600">Call Type</p>
                      <p className="font-medium">{session.call_type === 'IN' ? 'Incoming' : 'Outgoing'}</p>
                    </div>
                  )}
                  {session.call_status && (
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <p className="font-medium capitalize">{session.call_status}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Termination Information */}
              {session.termination_source && (
                <div className={`p-4 rounded-lg border ${
                  session.termination_source === 'user' ? 'bg-blue-50 border-blue-200' :
                  session.termination_source === 'ai' ? 'bg-purple-50 border-purple-200' :
                  'bg-gray-50 border-gray-200'
                }`}>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <XCircle className={`w-4 h-4 ${
                      session.termination_source === 'user' ? 'text-blue-600' :
                      session.termination_source === 'ai' ? 'text-purple-600' :
                      'text-gray-600'
                    }`} />
                    Call Termination
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Terminated By</p>
                      <p className="font-medium flex items-center gap-1">
                        {session.termination_source === 'user' && <User className="w-4 h-4" />}
                        {session.termination_source === 'ai' && <BotIcon className="w-4 h-4" />}
                        {session.termination_source === 'timeout' && <Clock className="w-4 h-4" />}
                        <span className="capitalize">{session.termination_source}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Transfer Information */}
              {session.transfer_type && (
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <PhoneForwarded className="w-4 h-4 text-orange-600" />
                    Transfer Information
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Transfer Type</p>
                      <p className="font-medium">
                        {session.transfer_type === 'ai' ? 'AI' : session.transfer_type === 'hotkey' ? 'Hotkey' : 'N/A'}
                      </p>
                    </div>
                    {session.transferred_to && (
                      <div>
                        <p className="text-sm text-gray-600">Transferred To</p>
                        <p className="font-medium">{session.transferred_to}</p>
                      </div>
                    )}
                    {session.transfer_status && (
                      <div>
                        <p className="text-sm text-gray-600">Transfer Status</p>
                        <p className="font-medium capitalize">{session.transfer_status.toLowerCase()}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* DTMF Collection */}
              {session.dtmf_collection && session.dtmf_collection.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Hash className="w-4 h-4 text-purple-600" />
                    DTMF Keys Pressed ({session.dtmf_collection.length})
                  </h4>
                  <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                    <div className="grid grid-cols-1 gap-2">
                      {session.dtmf_collection.map((dtmf, index) => (
                        <div key={index} className="flex items-center justify-between bg-white p-2 rounded border border-purple-100">
                          <span className="font-mono font-bold text-purple-700 text-lg">{dtmf.digit}</span>
                          <span className="text-xs text-gray-600">{dtmf.context}</span>
                          <span className="text-xs text-gray-500">{formatDate(dtmf.timestamp)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {session.call_summary && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Mic className="w-4 h-4 text-blue-600" />
                    Call Summary
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm">{maskSensitiveNumbers(session.call_summary)}</p>
                  </div>
                </div>
              )}

              {session.transfer_call_summary && (
                <div>
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <ArrowRightLeft className="w-4 h-4 text-orange-600" />
                    Transfer Call Summary
                  </h4>
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <p className="text-sm">{maskSensitiveNumbers(session.transfer_call_summary)}</p>
                  </div>
                </div>
              )}
              {/* What the call produced. Rendered only when there IS something:
                  most calls capture nothing, and an empty "Captured" panel would
                  read as a failure rather than as an ordinary quiet call. */}
              {captured.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">
                    {captured.length === 1 ? 'What this call captured' : `What this call captured (${captured.length})`}
                  </h4>
                  <div className="space-y-2">
                    {captured.map((c: any) => {
                      const type = c.captureType || c.capture_type || 'CAPTURE';
                      const name = c.displayName || c.display_name;
                      const sync = c.syncStatus || c.sync_status;
                      const crmId = c.crmRecordId || c.crm_record_id;
                      const crm = c.crmProvider || c.crm_provider;
                      const fields = c.fields || {};
                      const answered = Object.entries(fields)
                        .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '');
                      return (
                        <div key={c.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium px-2 py-0.5 rounded bg-indigo-50 text-indigo-700
                                               dark:bg-indigo-900/30 dark:text-indigo-300">{type}</span>
                              <span className="font-medium">{name || c.phone || '—'}</span>
                            </div>
                            {/* Says where it ended up. "In CRM" and "kept here"
                                are different facts and a reader acts on them
                                differently. */}
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              {sync === 'SYNCED' && crm ? `in ${crm}${crmId ? ` · ${crmId}` : ''}`
                                : sync === 'LOCAL_ONLY' ? 'kept in cpass only'
                                : sync === 'FAILED' ? 'not synced to the CRM'
                                : sync === 'QUEUED' ? 'queued for the CRM'
                                : 'no CRM connected'}
                            </span>
                          </div>
                          {answered.length > 0 && (
                            <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs">
                              {answered.slice(0, 10).map(([k, v]) => (
                                <div key={k} className="flex gap-1.5">
                                  <dt style={{ color: 'var(--text-muted)' }}>{k.replace(/_/g, ' ')}:</dt>
                                  <dd className="font-medium break-all">{String(v)}</dd>
                                </div>
                              ))}
                            </dl>
                          )}
                          {answered.length > 10 && (
                            <div className="mt-1 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                              and {answered.length - 10} more field{answered.length - 10 === 1 ? '' : 's'} —
                              open it under Leads &amp; Bookings for the rest.
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

 {/* Offered whenever the resolver has SOMETHING to work with — a stored URL
                  or a call id. Gating on recording_url alone hid the player on
                  exactly the portals where the call detail does not carry that
                  field, and hid it on cpass too, where a recording is found from
                  the call id. If nothing resolves, handlePlayRecording says so in
                  playbackError rather than the button silently doing nothing. */}
              {(call?.recording_url || (call as any)?.call_id || (call as any)?.unique_id
                || (session as any)?.call_id) && (
                <div>
                  <h4 className="font-semibold mb-2">Recording</h4>
                  <div className="bg-green-50 p-4 rounded-lg">
                    {!showPlayer ? (
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Audio Recording Available</p>
                          {/* Says why nothing played. Without this the button
                              simply did nothing and read as broken. */}
                          {playbackError && (
                            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
                              {playbackError}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={handlePlayRecording}
                            className="p-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <Play className="w-5 h-5" />
                          </button>
                          <button
                            onClick={handleDownloadRecording}
                            className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                          >
                            <Download className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* PLAYING MOVES THE CONTROL TO THE CONVERSATION.
                         The transport used to appear here, in this block, with
                         the transcript below a screenful of metadata — so you
                         could hear the call or read it, never both at once.
                         Once playing, the player renders in the transcript's
                         own slot (see the TranscriptViewer below) and this
                         block just says where it went. */
                      <div className="space-y-3">
                      {/* No conversation to sit inside — a call with audio but no
                          transcript is ordinary — so the transport stays here
                          rather than vanishing with the slot that would have
                          held it. */}
                      {!session.transcript && <AudioPlayer src={authenticatedUrl} autoPlay={pressedPlay} elementRef={audioEl} />}
                      <div className="flex items-center justify-between">
                        <p className="text-sm">
                          {session.transcript
                            ? 'Playing with the conversation below.'
                            : 'Now playing.'}
                        </p>
                        <div className="flex gap-2">
                          <button
                            onClick={handleDownloadRecording}
                            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                          >
                            <Download className="w-4 h-4" />
                            Download
                          </button>
                          <button
                            onClick={() => setShowPlayer(false)}
                            className="text-gray-500 hover:text-gray-700"
                            title="Stop playing"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* AI Conversation Transcript, with the audio in it. */}
              {session.transcript && (
                <TranscriptViewer
                  transcriptJson={session.transcript}
                  player={showPlayer && authenticatedUrl
                    ? <AudioPlayer src={authenticatedUrl} autoPlay={pressedPlay} />
                    : undefined}
                />
              )}

              {/* No conversation on the session row: cpass's transcript store (flag call-transcripts;
                  hidden on 404). Where the row has one, the viewer above already shows it. */}
              {!session.transcript && (
                <CallTranscriptPanel
                  callId={(call as any)?.call_id || (call as any)?.unique_id || (session as any)?.call_id}
                  organizationId={organizationId}
                  onSeek={showPlayer && authenticatedUrl ? (sec) => { seekAudio(audioEl.current, sec); } : undefined}
                />
              )}

              {/* Legacy Transcription (fallback) */}
              {session.transcription && !session.transcript && (
                <div>
                  <h4 className="font-semibold mb-2">Full Transcription</h4>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <pre className="text-sm whitespace-pre-wrap font-mono">{session.transcription}</pre>
                  </div>
                </div>
              )}

             
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioSessionModal;